import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Title,
  Paper,
  Stack,
  Select,
  Button,
  Group,
  Text,
  Loader,
  ScrollArea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import { PAGE_KEYS, PageKey } from '../utility/pageAccess';

type UserRow = {
  user_id: string;
  first_name: string;
  last_name: string;
  username: string;
  pages: Record<string, string>;
};

type ListResponse = {
  users: UserRow[];
};

const PAGE_COLUMN_KEYS: PageKey[] = [
  PAGE_KEYS.opportunity,
  PAGE_KEYS.kpis,
  PAGE_KEYS.campaigns,
  PAGE_KEYS.clients,
  PAGE_KEYS.contacts,
  PAGE_KEYS.tasks,
  PAGE_KEYS.products,
  PAGE_KEYS.inventory,
  PAGE_KEYS.balance,
  PAGE_KEYS.invoices,
];

function storedToSelectValue(stored: string | undefined): string {
  if (!stored) {
    return 'all';
  }
  return stored;
}

export default function AdminAccessPage() {
  const { t, i18n } = useTranslation();
  const { accessToken, actualEnterprise } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const enterpriseId = actualEnterprise?.id;

  const pageColumns = useMemo(
    () =>
      PAGE_COLUMN_KEYS.map((key) => ({
        key,
        label: t(`admin.pages.${key}`),
      })),
    [t, i18n.language],
  );

  const selectOptionsForPage = useCallback(
    (pageKey: PageKey) => {
      if (pageKey === PAGE_KEYS.opportunity) {
        return [
          { value: 'all', label: t('admin.access.allEnterprise') },
          { value: 'hidden', label: t('admin.access.hidden') },
          { value: 'owner_only', label: t('admin.access.ownerOnlyOpportunity') },
        ];
      }
      if (pageKey === PAGE_KEYS.kpis) {
        return [
          { value: 'all', label: t('admin.access.fullDefault') },
          { value: 'hidden', label: t('admin.access.hidden') },
          { value: 'owner_only', label: t('admin.access.ownerOnlyKpis') },
        ];
      }
      if (pageKey === PAGE_KEYS.balance) {
        return [
          { value: 'all', label: t('admin.access.fullDefault') },
          { value: 'hidden', label: t('admin.access.hidden') },
          { value: 'owner_only', label: t('admin.access.ownerOnlyBalance') },
        ];
      }
      if (pageKey === PAGE_KEYS.invoices) {
        return [
          { value: 'all', label: t('admin.access.fullDefault') },
          { value: 'hidden', label: t('admin.access.hidden') },
          { value: 'owner_only', label: t('admin.access.ownerOnlyInvoices') },
        ];
      }
      return [
        { value: 'all', label: t('admin.access.fullDefault') },
        { value: 'hidden', label: t('admin.access.hidden') },
      ];
    },
    [t, i18n.language],
  );

  const load = useCallback(async () => {
    if (!accessToken || !enterpriseId) {
      setUsers([]);
      setDrafts({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/page_access/${enterpriseId}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        },
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data: ListResponse = await response.json();
      setUsers(data.users);
      const initial: Record<string, Record<string, string>> = {};
      for (const u of data.users) {
        initial[u.user_id] = { ...u.pages };
      }
      setDrafts(initial);
    } catch (e) {
      notifications.show({
        title: t('admin.loadFailed'),
        message: e instanceof Error ? e.message : String(e),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [accessToken, enterpriseId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const setDraftPage = (userId: string, pageKey: PageKey, value: string | null) => {
    const v = value ?? 'all';
    setDrafts((prev) => {
      const base = { ...(prev[userId] ?? {}) };
      if (v === 'all') {
        delete base[pageKey];
      } else {
        base[pageKey] = v;
      }
      return { ...prev, [userId]: base };
    });
  };

  const saveUser = async (userId: string) => {
    if (!accessToken || !enterpriseId) return;
    const raw = drafts[userId] ?? {};
    const pages: Record<string, string> = {};
    for (const col of pageColumns) {
      const val = raw[col.key];
      if (val && val !== 'all') {
        pages[col.key] = val;
      }
    }
    setSavingId(userId);
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/page_access/${enterpriseId}/${userId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ pages }),
        },
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      notifications.show({ title: t('admin.saved'), message: t('admin.savedMessage'), color: 'green' });
      await load();
    } catch (e) {
      notifications.show({
        title: t('admin.saveFailed'),
        message: e instanceof Error ? e.message : String(e),
        color: 'red',
      });
    } finally {
      setSavingId(null);
    }
  };

  if (!enterpriseId) {
    return (
      <Paper p="md">
        <Text>{t('admin.selectEnterprise')}</Text>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Group position="center" p="xl">
        <Loader />
      </Group>
    );
  }

  return (
    <Stack spacing="md" p="md">
      <Title order={3}>{t('admin.title')}</Title>
      <Text size="sm" c="dimmed">
        {t('admin.description')}
      </Text>

      <ScrollArea type="scroll">
        <Stack spacing="lg">
          {users.length === 0 ? (
            <Paper p="md" withBorder>
              <Text size="sm">
                {t('admin.noUsers')}
              </Text>
            </Paper>
          ) : (
            users.map((u) => {
            const draft = drafts[u.user_id] ?? {};
            const displayName = `${u.first_name} ${u.last_name} (${u.username})`;
            return (
              <Paper key={u.user_id} p="md" withBorder shadow="xs">
                <Text fw={600} mb="sm">
                  {displayName}
                </Text>
                <Group align="flex-start" spacing="sm">
                  {pageColumns.map((col) => (
                    <Select
                      key={col.key}
                      label={col.label}
                      data={selectOptionsForPage(col.key)}
                      value={storedToSelectValue(draft[col.key] ?? u.pages[col.key])}
                      onChange={(val) => setDraftPage(u.user_id, col.key, val)}
                      w={260}
                      size="xs"
                    />
                  ))}
                </Group>
                <Button
                  mt="md"
                  size="xs"
                  loading={savingId === u.user_id}
                  onClick={() => void saveUser(u.user_id)}
                >
                  {t('admin.saveUser')}
                </Button>
              </Paper>
            );
          })
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
